import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    FruitfulCustomIcon,
    H1Regular,
    H5Emphasized,
    LabelRegular,
    Private,
    Shadows,
    type StackProps,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import {
    DEFAULT_SAVINGS_ACCOUNT_NICKNAME,
    RelationshipStatus,
} from '../../types/BankAccount';

export const SPACE_CARD_HORIZONTAL_MARGIN = 8;

interface SavingsSpaceCardProps extends StackProps {
    bankAccountDetail:
        | BankAccountDisplayDetail
        | Partial<BankAccountDisplayDetail>;
    size?: 'sm' | 'md';
    isSelected?: boolean;
    onPressToScrollTo?: (x: number, y: number) => void;
}

const LegacySpaceCard = ({
    bankAccountDetail,
    size = 'md',
    isSelected,
    onPressToScrollTo,
    ...props
}: SavingsSpaceCardProps) => {
    // Use primitive state value types so that value changes
    // are correctly handled in any hook dependencies
    const [coordX, setCoordX] = useState(0);
    const [coordY, setCoordY] = useState(0);

    const {
        availableToWithdraw,
        interestEarned,
        nickname,
        jointUserRelationshipStatus,
        icon,
        color,
    } = bankAccountDetail;

    const isJointAccount =
        jointUserRelationshipStatus === RelationshipStatus.ACTIVE;

    useEffect(() => {
        if (isSelected) {
            onPressToScrollTo?.(coordX, coordY);
        }
    }, [isSelected, coordX, coordY]);

    return (
        <YStack
            height={size === 'sm' ? 130 : 200}
            minWidth={size === 'sm' ? 240 : 280}
            maxWidth={size === 'sm' ? 260 : undefined}
            backgroundColor="$white"
            borderWidth={1}
            borderColor="$gray200"
            padding={12}
            borderRadius={size === 'sm' ? 12 : 14}
            onLayout={(event) => {
                setCoordX(
                    event.nativeEvent.layout.x - SPACE_CARD_HORIZONTAL_MARGIN, // Manually account for margin
                );
                setCoordY(event.nativeEvent.layout.y);
            }}
            onPress={(event) => {
                onPressToScrollTo?.(coordX, coordY);
                props.onPress?.(event);
            }}
            hoverStyle={{ cursor: 'pointer', backgroundColor: '$gray100' }}
            style={{ ...Shadows.SMALL }}
            {...props}
        >
            <YStack flex={1} justifyContent="space-between">
                <View>
                    <XStack
                        alignItems="flex-start"
                        justifyContent="space-between"
                    >
                        {size === 'sm' ? (
                            <YStack maxWidth="80%" gap={2}>
                                <BodyMediumEmphasized numberOfLines={2}>
                                    {nickname ??
                                        DEFAULT_SAVINGS_ACCOUNT_NICKNAME}
                                </BodyMediumEmphasized>
                                {isJointAccount && (
                                    <LabelRegular color="$gray900">
                                        Joint
                                    </LabelRegular>
                                )}
                            </YStack>
                        ) : (
                            <YStack maxWidth="80%" gap={2}>
                                <H5Emphasized numberOfLines={2}>
                                    {nickname ??
                                        DEFAULT_SAVINGS_ACCOUNT_NICKNAME}
                                </H5Emphasized>
                                {isJointAccount && (
                                    <BodyMediumRegular color="$gray900">
                                        Joint
                                    </BodyMediumRegular>
                                )}
                            </YStack>
                        )}
                        <FruitfulCustomIcon
                            size={size}
                            icon={icon}
                            color={color}
                        />
                    </XStack>
                </View>

                <Private>
                    {size === 'sm' ? (
                        <YStack gap={4}>
                            <H5Emphasized>{availableToWithdraw}</H5Emphasized>
                            <LabelRegular color="$green1000">
                                +{interestEarned} interest earned this month
                            </LabelRegular>
                        </YStack>
                    ) : (
                        <YStack gap={8}>
                            <H1Regular>{availableToWithdraw}</H1Regular>
                            <LabelRegular color="$green1000">
                                +{interestEarned} interest earned this month
                            </LabelRegular>
                        </YStack>
                    )}
                </Private>
            </YStack>
        </YStack>
    );
};

export default LegacySpaceCard;
